<script lang="ts">
	import BackgroundJobIndicator from '$lib/components/BackgroundJobIndicator.svelte';
	import LegacyLayout from '$lib/components/layout/LegacyLayout.svelte';
	import MarketingLayout from '$lib/components/layout/MarketingLayout.svelte';
	import ModernLayout from '$lib/components/layout/ModernLayout.svelte';
	import AnnouncementContainer from '$lib/components/ui/AnnouncementContainer.svelte';
	import NotificationContainer from '$lib/components/ui/NotificationContainer.svelte';
	import { enhancedCartStore } from '$lib/stores/enhancedCart.svelte';
	import type { Market } from '$lib/types/market';
	import type { User } from '$lib/types/user';
	import '../app.css';
	import { onMount } from 'svelte';

	let { data, children } = $props<{
		title: string;
		loggedIn: boolean;
		market: Market;
		user: User;
		isModernPage: boolean;
		flash: { notice?: string; warning?: string };
		isStripePage?: boolean;
		children: () => import('svelte').Snippet;
	}>();

	let isShowcasePage = $derived(data.isShowcasePage);
	let isModernPage = $derived(!isShowcasePage && data.isModernPage);
	let isMarketingPage = $derived(!data.market && !data.isModernPage && !isShowcasePage);

	// Force modern layout for error pages
	let forceModernForError = $state(false);

	$effect(() => {
		// Only run in browser
		if (typeof document === 'undefined') return;

		// Check if we're on an error page by looking for error elements
		const errorElement = document.querySelector('[data-error]');
		forceModernForError = !!errorElement;
	});

	// Override isModernPage if we're on an error page
	let finalIsModernPage = $derived(forceModernForError || isModernPage);

	// Initialize the cart store with user and market context
	$effect(() => {
		const userId = data.user?.id || null;
		const marketId = data.market?.id || null;
		enhancedCartStore.initialize(userId, marketId);
	});

	// Deployment version tracking for cache invalidation
	onMount(() => {
		// Get deployment info from environment variables injected at build time
		const deploymentHash =
			import.meta.env.VITE_COMMIT_HASH ||
			import.meta.env.VITE_DO_APP_COMMIT_SHA ||
			import.meta.env.VITE_APP_VERSION ||
			'unknown';

		const cachedVersion = sessionStorage.getItem('deploymentVersion');

		// If version changed, clear reload counters to allow fresh start
		if (cachedVersion && cachedVersion !== deploymentHash && deploymentHash !== 'unknown') {
			console.log('New deployment detected, clearing error recovery state');
			sessionStorage.setItem('chunkReloadCount', '0');
			sessionStorage.setItem('lastChunkReload', '0');
		}

		// Update cached version
		if (deploymentHash !== 'unknown') {
			sessionStorage.setItem('deploymentVersion', deploymentHash);
		}
	});
</script>

{#if isShowcasePage}
	<!-- Showcase pages render directly without any wrapper layout -->
	{@render children()}
{:else if finalIsModernPage}
	<ModernLayout title={data.title} market={data.market}>
		{@render children()}
	</ModernLayout>
{:else if isMarketingPage}
	<MarketingLayout title={data.title} user={data.user}>
		{@render children()}
	</MarketingLayout>
{:else}
	<LegacyLayout
		title={data.title}
		loggedIn={data.loggedIn}
		market={data.market}
		user={data.user}
		flash={data.flash}
	>
		{@render children()}
	</LegacyLayout>
{/if}

<!-- Global notification container -->
<NotificationContainer />

<!-- Global announcement container -->
<AnnouncementContainer user={data.user} />

<!-- Background job indicator -->
<BackgroundJobIndicator />
